import {
  allocateCartStock,
  orderByStockPriority,
} from '@/features/Product/services/productAvailability';
import type { CartItemQuantity } from '@/features/Cart/types/models';
import type { ShopifyCart, ShopifyCartItem } from '@/features/Cart/types/servicesShopifyCart';

type CartStockAdjustmentOptions = {
  unavailableLineKeys: string[];
  protectedLineIdentities: string[];
  isFreeGiftItem: (item: ShopifyCartItem) => boolean;
  getExactStock: (variantId: number) => number | undefined;
};

export const getCartStockAdjustments = (
  cart: ShopifyCart,
  {
    unavailableLineKeys,
    protectedLineIdentities,
    isFreeGiftItem,
    getExactStock,
  }: CartStockAdjustmentOptions,
): CartItemQuantity[] => {
  const isUnavailable = ({ key }: ShopifyCartItem) => unavailableLineKeys.includes(key);
  const isRepairable = (item: ShopifyCartItem) => !isFreeGiftItem(item);
  const removals = cart.items
    .filter((item) => isUnavailable(item) && isRepairable(item))
    .map(({ key }) => ({ key, quantity: 0 }));
  const lines = orderByStockPriority(
    cart.items.filter((item) => !isUnavailable(item)),
    new Set(protectedLineIdentities),
  );
  const allocations = allocateCartStock(lines, getExactStock);
  const reductions = lines.filter(isRepairable).flatMap(({ key, quantity }) => {
    const allocatedQuantity = allocations.get(key) ?? quantity;
    return allocatedQuantity < quantity ? [{ key, quantity: allocatedQuantity }] : [];
  });
  return [...removals, ...reductions];
};
